/**
 * Pure helpers for building authenticated fetch options and classifying responses.
 * Used by ssm-web4 and ssm-admin fetchInstance; app-specific token and 401/400
 * handling remain in each app.
 */

const DEFAULT_JSON_HEADERS = {
    'Content-Type': 'application/json',
    'Cache-Control': 'max-age=0, no-cache, must-revalidate, proxy-revalidate',
};

/**
 * Build fetch options with default JSON headers and Authorization.
 * Pure: (options, authToken) → options object for fetch().
 *
 * @param {Object} options - Request options (method, headers, token, body, and any other fetch options)
 * @param {string} [options.method] - HTTP method
 * @param {Object} [options.headers={}] - Additional headers (merged after defaults)
 * @param {string} [options.token] - Optional override for Authorization header
 * @param {*} [options.body] - Request body
 * @param {string} authToken - Primary authorization value (e.g. "Bearer <token>")
 * @returns {Object} Options suitable for fetch(url, result)
 */
export const buildAuthenticatedFetchOptions = (options, authToken) => {
    const { method, headers = {}, token, body, ...rest } = options;
    return {
        ...rest,
        method,
        headers: {
            ...DEFAULT_JSON_HEADERS,
            ...headers,
            Authorization: token ?? authToken,
        },
        body: body ?? {},
    };
};

/**
 * Build full URL from base URL and endpoint. Normalizes slashes to avoid double slashes.
 *
 * @param {string} baseURL - Base URL (e.g. "https://api.example.com" or "https://api.example.com/")
 * @param {string} endpoint - Path (e.g. "/users" or "users")
 * @returns {string} Full URL
 */
export const buildApiUrl = (baseURL, endpoint) => {
    let base = baseURL;
    while (base.length > 0 && base.endsWith('/')) {
        base = base.slice(0, -1);
    }
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${base}${path}`;
};

/**
 * Response status categories used by fetchInstance 401/400 handling.
 * @type {Readonly<{OK: string, BAD_REQUEST: string, UNAUTHORIZED: string}>}
 */
export const RESPONSE_STATUS = Object.freeze({
    UNAUTHORIZED: 'unauthorized',
    BAD_REQUEST: 'bad_request',
    OK: 'ok',
});

/**
 * Classify a fetch Response by status for branching (e.g. 401 → refresh/logout, 400 → reject).
 * Pure: only reads response.status.
 *
 * @param {Response|null|undefined} response - Fetch Response or falsy
 * @returns {string} One of RESPONSE_STATUS values
 */
export const getResponseStatusCategory = (response) => {
    if (!response || typeof response.status !== 'number') {
        return RESPONSE_STATUS.OK;
    }
    if (response.status === 401) return RESPONSE_STATUS.UNAUTHORIZED;
    if (response.status === 400) return RESPONSE_STATUS.BAD_REQUEST;
    return RESPONSE_STATUS.OK;
};
